import {
	ChevronLeft,
	ChevronRight,
	Globe,
	Rss,
	Search,
	ShoppingBag,
	Store,
	X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { PageHeader } from "../components/layout/PageHeader";
import { ShopTextBulkPanel } from "../components/merchants/ShopTextBulkPanel";
import { TIERS, tierColor, tierLabel } from "../constants/tiers";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface MerchantListItem {
	id: number;
	name: string;
	website: string;
	tier: string;
	validated: boolean;
	logoUrl: string | null;
	contactEmail: string | null;
	contactPerson: string | null;
	city: string | null;
	country: string | null;
	commissionRate: string | null;
	trustpilotRating: string | null;
	createdAt: string;
	updatedAt: string;
	feedCount: number;
	productCount: number;
}

interface MerchantsResponse {
	items: MerchantListItem[];
	total: number;
	page: number;
	totalPages: number;
}

// ---------------------------------------------------------------------------
// Merchants page
// ---------------------------------------------------------------------------
const Merchants = () => {
	const [merchants, setMerchants] = useState<MerchantListItem[]>([]);
	const [loading, setLoading] = useState(true);
	const [imgDims, setImgDims] = useState<
		Record<number, { w: number; h: number }>
	>({});
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [total, setTotal] = useState(0);
	const [search, setSearch] = useState("");
	const [searchInput, setSearchInput] = useState("");
	const [tierFilter, setTierFilter] = useState<string>("");
	const [validatedFilter, setValidatedFilter] = useState<string>("");

	const fetchMerchants = useCallback(async () => {
		setLoading(true);
		try {
			const params: Record<string, string> = { page: String(page) };
			if (search) params.q = search;
			if (tierFilter) params.tier = tierFilter;
			if (validatedFilter) params.validated = validatedFilter;

			const { data } = await api.merchants.get({ query: params });
			if (data) {
				const d = data as unknown as MerchantsResponse;
				setMerchants(d.items);
				setTotalPages(d.totalPages);
				setTotal(d.total);
			}
		} catch {
			// ignore
		} finally {
			setLoading(false);
		}
	}, [page, search, tierFilter, validatedFilter]);

	useEffect(() => {
		fetchMerchants();
	}, [fetchMerchants]);

	const handleSearch = (e: React.FormEvent) => {
		e.preventDefault();
		setPage(1);
		setSearch(searchInput);
	};

	return (
		<div className="space-y-6">
			<PageHeader
				kicker="Shop · Merchants"
				title={`${total.toLocaleString("en-US")} merchants.`}
				qualifier="Every shop, listed or not."
				description="Listing follows payment status and is re-checked every night. Affiliates are synced from Partner-ads and Adtraction."
			/>

			<ShopTextBulkPanel />

			{/* Search bar + filters */}
			<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
				<form onSubmit={handleSearch} className="relative flex-1 max-w-xl">
					<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
					<input
						type="text"
						placeholder="Search merchants by name, website, email…"
						value={searchInput}
						onChange={(e) => setSearchInput(e.target.value)}
						className="w-full rounded-full border border-zinc-200 bg-white py-2.5 pr-4 pl-10 text-sm text-zinc-800 outline-none transition-colors placeholder:text-zinc-400 focus:border-zinc-400"
					/>
					{search && (
						<button
							type="button"
							onClick={() => {
								setSearch("");
								setSearchInput("");
								setPage(1);
							}}
							className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
						>
							<X className="w-4 h-4" />
						</button>
					)}
				</form>

				<div className="flex items-center gap-2 flex-wrap">
					{/* Tier filter */}
					<select
						value={tierFilter}
						onChange={(e) => {
							setTierFilter(e.target.value);
							setPage(1);
						}}
						className="px-3 py-1.5 rounded-full text-xs font-semibold border border-slate-200 bg-white text-slate-600 cursor-pointer outline-none focus:border-primary/40"
					>
						<option value="">All Tiers</option>
						{TIERS.map((t) => (
							<option key={t} value={t}>
								{tierLabel(t)}
							</option>
						))}
					</select>

					{/* Validated filter */}
					<select
						value={validatedFilter}
						onChange={(e) => {
							setValidatedFilter(e.target.value);
							setPage(1);
						}}
						className="px-3 py-1.5 rounded-full text-xs font-semibold border border-slate-200 bg-white text-slate-600 cursor-pointer outline-none focus:border-primary/40"
					>
						<option value="">All Status</option>
						<option value="true">Listed</option>
						<option value="false">Not listed</option>
					</select>
				</div>
			</div>

			{/* Table */}
			{loading ? (
				<div className="flex items-center justify-center py-20">
					<div className="h-8 w-8 animate-spin rounded-full border-3 border-zinc-200 border-t-zinc-900" />
				</div>
			) : merchants.length === 0 ? (
				<div className="flex flex-col items-center justify-center py-20 text-slate-400">
					<Store className="w-16 h-16 mb-4 text-slate-300" />
					<p className="text-lg font-semibold text-slate-500">
						No merchants found
					</p>
					<p className="text-sm">Try adjusting your search or filters</p>
				</div>
			) : (
				<div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
					<div className="overflow-x-auto">
						<table className="w-full text-sm">
							<thead>
								<tr className="border-zinc-200 border-b text-left font-medium text-[0.68rem] text-zinc-500 uppercase tracking-[0.14em]">
									<th className="px-6 py-3">Merchant</th>
									<th className="px-6 py-3">Website</th>
									<th className="px-6 py-3">Tier</th>
									<th className="px-6 py-3">Status</th>
									<th className="px-6 py-3">Feeds</th>
									<th className="px-6 py-3">Products</th>
									<th className="px-6 py-3">Commission</th>
									<th className="px-6 py-3">Contact</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-slate-100">
								{merchants.map((m) => (
									<tr
										key={m.id}
										className="hover:bg-slate-50/50 transition-colors"
									>
										<td className="px-6 py-3.5">
											<Link
												to={`/merchants/${m.id}`}
												className="flex items-center gap-3 group"
											>
												{m.logoUrl ? (
													<div className="relative">
														<img
															src={m.logoUrl}
															alt={m.name}
															className="w-9 h-9 rounded-lg object-contain border border-slate-100"
															onLoad={(e) => {
																const img = e.currentTarget;
																setImgDims((prev) => ({
																	...prev,
																	[m.id]: {
																		w: img.naturalWidth,
																		h: img.naturalHeight,
																	},
																}));
															}}
														/>
														{imgDims[m.id] && (
															<span className="absolute -bottom-1 -right-1 bg-slate-700/80 text-white text-[8px] leading-none px-1 py-0.5 rounded font-mono whitespace-nowrap">
																{imgDims[m.id].w}×{imgDims[m.id].h}
															</span>
														)}
													</div>
												) : (
													<div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
														<Store className="w-4 h-4 text-slate-400" />
													</div>
												)}
												<span className="font-medium text-zinc-900 group-hover:underline">
													{m.name}
												</span>
											</Link>
										</td>
										<td className="px-6 py-3.5">
											<a
												href={
													m.website.startsWith("http")
														? m.website
														: `https://${m.website}`
												}
												target="_blank"
												rel="noopener noreferrer"
												className="text-slate-500 hover:text-primary transition-colors flex items-center gap-1 text-xs"
											>
												<Globe className="w-3 h-3" />
												{m.website.replace(/^https?:\/\//, "")}
											</a>
										</td>
										<td className="px-6 py-3.5">
											<span
												className={`inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${tierColor(m.tier)}`}
											>
												{tierLabel(m.tier)}
											</span>
										</td>
										<td className="px-6 py-3.5">
											<span className="flex items-center gap-2 whitespace-nowrap text-[13px] text-zinc-700">
												<span
													className={`size-1.5 rounded-full ${m.validated ? "bg-emerald-500" : "bg-red-500"}`}
												/>
												{m.validated ? "Listed" : "Not listed"}
											</span>
										</td>
										<td className="px-6 py-3.5">
											<span className="flex items-center gap-1 text-slate-500 text-xs">
												<Rss className="w-3 h-3" />
												{m.feedCount}
											</span>
										</td>
										<td className="px-6 py-3.5">
											<span className="flex items-center gap-1 text-slate-500 text-xs">
												<ShoppingBag className="w-3 h-3" />
												{m.productCount.toLocaleString()}
											</span>
										</td>
										<td className="px-6 py-3.5">
											{m.commissionRate ? (
												<span className="text-xs font-medium text-slate-600">
													{m.commissionRate}%
												</span>
											) : (
												<span className="text-xs text-slate-300">–</span>
											)}
										</td>
										<td className="px-6 py-3.5">
											{m.contactEmail ? (
												<span className="text-xs text-slate-500">
													{m.contactEmail}
												</span>
											) : (
												<span className="text-xs text-slate-300">–</span>
											)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{/* Pagination */}
					{totalPages > 1 && (
						<div className="flex items-center justify-between px-6 py-3 border-t border-slate-100">
							<p className="text-xs text-slate-400">
								Page {page} of {totalPages}
							</p>
							<div className="flex items-center gap-1">
								<button
									type="button"
									onClick={() => setPage((p) => Math.max(1, p - 1))}
									disabled={page === 1}
									className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 text-slate-600 hover:border-primary/40 hover:text-primary transition-colors disabled:opacity-40 disabled:pointer-events-none"
								>
									<ChevronLeft className="w-4 h-4" />
								</button>
								{Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
									const start = Math.max(1, Math.min(page - 2, totalPages - 4));
									const p = start + i;
									if (p > totalPages) return null;
									return (
										<button
											key={p}
											type="button"
											onClick={() => setPage(p)}
											className={`flex items-center justify-center w-8 h-8 rounded-lg border text-sm font-medium transition-colors ${
												p === page
													? "bg-primary text-white border-primary"
													: "border-slate-200 text-slate-600 hover:border-primary/40 hover:text-primary"
											}`}
										>
											{p}
										</button>
									);
								})}
								<button
									type="button"
									onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
									disabled={page === totalPages}
									className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 text-slate-600 hover:border-primary/40 hover:text-primary transition-colors disabled:opacity-40 disabled:pointer-events-none"
								>
									<ChevronRight className="w-4 h-4" />
								</button>
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default Merchants;
